import './PreviewRelationUser.scss';

import { Cancel, Check, Delete } from '@mui/icons-material';
import { Chip, IconButton } from '@mui/material';
import classNames from 'classnames';
import { setModalConfirmOpen } from 'domain/modal/store/ModalSlice';
import { RelationTypeEnum } from 'domain/relation/RelationTypeEnum';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useTranslate } from 'primary/hooks/useTranslate';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Relation } from '../../domain/relation/Relation';
import { RootState } from '../../store';
import { WrapperOtherProfilPhotoOverview } from '../Components/User/WrapperOtherProfilPhotoOverview';
import { useContextDependency } from '../hooks/useContextDependency';

type PreviewRelationUserProps = {
  relation: Relation;
  onClick?: (relation: Relation | null) => void;
  isSelected: boolean;
  type: 'BLOCKED' | 'FRIENDS' | 'WAITING';
  retry: () => void;
};

export const PreviewRelationUser = ({
  relation,
  onClick,
  isSelected,
  type,
  retry,
}: PreviewRelationUserProps) => {
  const user = useSelector((state: RootState) => state.user.connectedUser);
  const [isLoading, setIsLoading] = useState(false);
  const { relationRepository } = useContextDependency();

  const isInvited = relation.invited.idPlayer === user?.userProfil.idPlayer;

  const playerToDisplay = isInvited ? relation.owner : relation.invited;
  const strTranslate =
    relation.type === RelationTypeEnum.BLOCK
      ? 'confirmModalDeleteBlock'
      : 'confirmModalDeleteFriend';
  const t = useTranslate();
  const handleDeleteAction = () => {
    setIsLoading(true);
    relationRepository.deleteById(relation.id).then(() => {
      sendEventToastMessage(t('general.edit.success'), 'success');
      retry();
    });
  };

  const dispatch = useDispatch();

  const openModalConfirmCancelInvite = (e: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    dispatch(
      setModalConfirmOpen({
        props: {
          title: t(`relations.${strTranslate}.title`, {
            name: playerToDisplay.idPlayer,
          }),
          labelDecline: t(`relations.${strTranslate}.cancelBtnText`),
          labelAccept: t(`relations.${strTranslate}.confirmBtnText`),
          description: t(`relations.${strTranslate}.confirmText`, {
            name: playerToDisplay.idPlayer,
          }),
          action: handleDeleteAction,
        },
      }),
    );
  };

  const openModalConfirmDelete = (e: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    dispatch(
      setModalConfirmOpen({
        props: {
          title: t(`relations.${strTranslate}.title`, {
            name: playerToDisplay.idPlayer,
          }),
          labelDecline: t(`relations.${strTranslate}.cancelBtnText`),
          labelAccept: t(`relations.${strTranslate}.confirmBtnText`),
          description: t(`relations.${strTranslate}.confirmText`, {
            name: playerToDisplay.idPlayer,
          }),
          action: handleDeleteAction,
        },
      }),
    );
  };

  return (
    <div
      className={classNames('previewRelationUser', { isSelected: isSelected })}
      onClick={(e) => {
        isSelected ? onClick?.(null) : onClick?.(relation);
        return e;
      }}
    >
      <div className={classNames('previewRelationUser__infos')}>
        <div className={'previewRelationUser__infos-photo'}>
          <WrapperOtherProfilPhotoOverview
            size={'small'}
            displayName={playerToDisplay.idPlayer}
            photo={playerToDisplay.publicPhotoUrl}
            className={classNames(
              'photo',
              '-photo',
              playerToDisplay.neon?.getNeonBorderClassName(),
            )}
            idUser={playerToDisplay.id as number}
          />
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          }}
        >
          <span className={'previewRelationUser__infos-name'}>
            {playerToDisplay.idPlayer}
          </span>
          {relation.type !== RelationTypeEnum.BLOCK && type === 'WAITING' && (
            <div>
              <Chip
                style={{ marginRight: '8px' }}
                size={'small'}
                label={isInvited ? 'Reçue' : 'Envoyé'}
              />
            </div>
          )}
        </div>
      </div>
      <div className={'-actions'}>
        {(type === 'FRIENDS' || type === 'BLOCKED') && (
          <IconButton
            disabled={isLoading}
            style={{ marginLeft: '8px', marginRight: '8px' }}
            size={'small'}
            onClick={openModalConfirmDelete}
            color={'error'}
          >
            <Delete />
          </IconButton>
        )}
        {type === 'WAITING' && !isInvited && (
          <IconButton
            disabled={isLoading}
            style={{ marginLeft: '8px', marginRight: '8px' }}
            size={'small'}
            color={'error'}
            onClick={openModalConfirmCancelInvite}
          >
            <Cancel />
          </IconButton>
        )}
        {type === 'WAITING' && isInvited && (
          <>
            <IconButton
              disabled={isLoading}
              style={{ marginLeft: '8px', marginRight: '8px' }}
              size={'small'}
              color={'primary'}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                relationRepository.responseInvitation(relation.id, true);
              }}
            >
              <Check />
            </IconButton>
            <IconButton
              disabled={isLoading}
              style={{ marginLeft: '8px', marginRight: '8px' }}
              size={'small'}
              color={'error'}
              onClick={openModalConfirmDelete}
            >
              <Cancel />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
};
